import { useState } from 'react';
import { AnimatePresence, motion } from 'framer-motion';
import { Camera, Upload, X, ChevronDown, Leaf, Loader2 } from 'lucide-react';
import { toast } from 'sonner';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { ARBOREAL, IRRIGATION, MANAGEMENT, PRUNING } from './yieldConstants';
import { compressImage } from './yieldApi';

const MAX_PHOTOS = 8;

function OptSelect({ label, value, onChange, options, testId }) {
  return (
    <div>
      <Label className="text-xs">{label}</Label>
      <Select value={value || '__none__'} onValueChange={v => onChange(v === '__none__' ? '' : v)}>
        <SelectTrigger data-testid={testId} className="mt-1.5"><SelectValue/></SelectTrigger>
        <SelectContent>
          <SelectItem value="__none__">— nessun dato —</SelectItem>
          {options.map(o => <SelectItem key={o.v} value={o.v}>{o.l}</SelectItem>)}
        </SelectContent>
      </Select>
    </div>
  );
}

function NumField({ label, k, form, set, placeholder, testId, step }) {
  return (
    <div>
      <Label className="text-xs">{label}</Label>
      <Input type="number" step={step} min="0" placeholder={placeholder} value={form[k]} onChange={e => set(k, e.target.value)} data-testid={testId} className="mt-1.5"/>
    </div>
  );
}

export function StepFinish({ form, set, photos, setPhotos }) {
  const [open, setOpen] = useState(false);
  const [busy, setBusy] = useState(false);
  const arboreal = ARBOREAL.includes(form.crop);

  const addPhotos = async (files) => {
    const arr = Array.from(files || []).slice(0, MAX_PHOTOS - photos.length);
    if (!arr.length) return toast.error(`Massimo ${MAX_PHOTOS} foto`);
    setBusy(true);
    const out = [];
    for (const f of arr) {
      try { out.push({ preview: URL.createObjectURL(f), b64: await compressImage(f) }); }
      catch { toast.error(`"${f.name}": formato non supportato (usa JPG o PNG)`); }
    }
    setPhotos(p => [...p, ...out].slice(0, MAX_PHOTOS));
    setBusy(false);
  };

  return (
    <div className="space-y-5">
      <div>
        <div className="flex items-center gap-2 text-sm font-medium"><Camera size={16} className="text-chartreuse"/> Foto del campo <span className="text-muted-foreground font-normal">(facoltative, consigliate)</span></div>
        <p className="text-xs text-muted-foreground mt-1">Una foto d'insieme, un primo piano dei frutti e una della pianta bastano per alzare la confidenza.</p>
        <label className="mt-3 flex flex-col items-center justify-center cursor-pointer rounded-2xl border border-dashed border-chartreuse/30 p-5 text-center hover:bg-chartreuse/5 transition-colors" data-testid="yield-photo-dropzone">
          <input type="file" accept="image/*" multiple hidden onChange={e => { addPhotos(e.target.files); e.target.value = ''; }} data-testid="yield-photo-input"/>
          {busy ? <Loader2 size={20} className="text-chartreuse animate-spin"/> : <Upload size={20} className="text-chartreuse"/>}
          <div className="text-sm mt-2">{busy ? 'Ottimizzo le foto…' : 'Scatta o carica foto'}</div>
          <div className="text-xs text-muted-foreground mt-0.5" data-testid="yield-photo-count">{photos.length}/{MAX_PHOTOS} · compresse automaticamente</div>
        </label>
        {photos.length > 0 && (
          <div className="mt-3 grid grid-cols-4 sm:grid-cols-5 gap-2">
            {photos.map((p, i) => (
              <div key={p.preview} className="relative aspect-square rounded-xl overflow-hidden bg-secondary">
                <img src={p.preview} alt="" className="w-full h-full object-cover"/>
                <button type="button" onClick={() => setPhotos(ps => ps.filter((_, j) => j !== i))} data-testid={`yield-photo-remove-${i}`} aria-label="Rimuovi"
                        className="absolute top-1 right-1 bg-foreground/90 text-background rounded-full p-1 hover:scale-110 transition-transform"><X size={10}/></button>
              </div>
            ))}
          </div>
        )}
      </div>

      <div className="rounded-2xl border border-border">
        <button type="button" onClick={() => setOpen(o => !o)} data-testid="yield-advanced-toggle"
                className="w-full flex items-center gap-2 px-4 h-12 text-sm font-medium">
          <Leaf size={15} className="text-chartreuse"/> Dettagli avanzati <span className="text-muted-foreground font-normal">(facoltativi · riducono l'errore)</span>
          <ChevronDown size={16} className={`ml-auto transition-transform ${open ? 'rotate-180' : ''}`}/>
        </button>
        <AnimatePresence initial={false}>
          {open && (
            <motion.div initial={{ height: 0, opacity: 0 }} animate={{ height: 'auto', opacity: 1 }} exit={{ height: 0, opacity: 0 }} className="overflow-hidden">
              <div className="px-4 pb-4 grid grid-cols-1 sm:grid-cols-2 gap-3">
                {arboreal && <NumField label="Età piante (anni)" k="plant_age_years" form={form} set={set} placeholder="es. 25" testId="yield-age-input"/>}
                {arboreal && <NumField label="Densità (piante/ha)" k="planting_density" form={form} set={set} placeholder="es. 300" testId="yield-density-input"/>}
                <OptSelect label="Irrigazione" value={form.irrigation} onChange={v => set('irrigation', v)} options={IRRIGATION} testId="yield-irrigation-select"/>
                <OptSelect label="Conduzione" value={form.management} onChange={v => set('management', v)} options={MANAGEMENT} testId="yield-management-select"/>
                {arboreal && <OptSelect label="Potatura" value={form.pruning} onChange={v => set('pruning', v)} options={PRUNING} testId="yield-pruning-select"/>}
                <NumField label="Resa anno scorso (q/ha)" k="last_year_yield_qha" form={form} set={set} placeholder="es. 28" testId="yield-lastyear-input" step="0.1"/>
              </div>
            </motion.div>
          )}
        </AnimatePresence>
      </div>
    </div>
  );
}
